/**
 * CreateTicketForm - Form to create a new support ticket
 */

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Heading, Text } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { useCallback, useState } from 'react';
import { createTicket } from '../api';
import type { TicketPriority } from '../types';

interface CreateTicketFormProps {
  onSuccess?: (ticketId: string) => void;
  onCancel?: () => void;
  className?: string;
}

const CATEGORIES = [
  { value: 'general', label: 'General Question' },
  { value: 'account', label: 'Account & Profile' },
  { value: 'deposits', label: 'Deposits & Funding' },
  { value: 'withdrawals', label: 'Withdrawals & Payouts' },
  { value: 'transactions', label: 'Transactions' },
  { value: 'verification', label: 'KYC / Verification' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'billing', label: 'Billing & Fees' },
];

const PRIORITIES: { value: TicketPriority; label: string; description: string }[] = [
  { value: 'low', label: 'Low', description: 'Not urgent' },
  { value: 'normal', label: 'Normal', description: 'Standard response time' },
  { value: 'high', label: 'High', description: 'Important issue' },
  { value: 'urgent', label: 'Urgent', description: 'Critical, needs immediate attention' },
];

export function CreateTicketForm({
  onSuccess,
  onCancel,
  className,
}: CreateTicketFormProps) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState<TicketPriority>('normal');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = subject.trim().length >= 3 && message.trim().length >= 10;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await createTicket({
        subject: subject.trim(),
        message: message.trim(),
        category,
        priority,
      });

      if (!result.success) {
        setError(result.error || 'Failed to create ticket');
        return;
      }

      if (result.ticket_id && onSuccess) {
        onSuccess(result.ticket_id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [canSubmit, isLoading, subject, message, category, priority, onSuccess]);

  return (
    <Card className={cn('p-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Heading variant="h3">Create Support Ticket</Heading>
        {onCancel && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            className="h-8 w-8"
            aria-label="Cancel"
          >
            <SFIcon icon={sfIcons.close} className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <Text className="text-destructive text-sm">{error}</Text>
        </div>
      )}

      {/* Form */}
      <div className="space-y-4">
        {/* Subject */}
        <div className="space-y-2">
          <Label htmlFor="ticket-subject">Subject *</Label>
          <Input
            id="ticket-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief description of your issue"
            disabled={isLoading}
            maxLength={200}
          />
          <Text variant="xs" color="muted">
            Minimum 3 characters
          </Text>
        </div>

        {/* Category */}
        <div className="space-y-2">
          <Label htmlFor="ticket-category">Category</Label>
          <select
            id="ticket-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={isLoading}
            className={cn(
              'w-full h-10 px-3 rounded-md',
              'border border-input bg-background',
              'text-sm',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            {CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        {/* Priority */}
        <div className="space-y-2">
          <Label>Priority</Label>
          <div className="grid grid-cols-2 gap-2">
            {PRIORITIES.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setPriority(p.value)}
                disabled={isLoading}
                className={cn(
                  'p-3 rounded-lg border text-left',
                  semanticTokens.motion.fast,
                  priority === p.value
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
              >
                <Text variant="sm" className="font-medium">
                  {p.label}
                </Text>
                <Text variant="xs" color="muted">
                  {p.description}
                </Text>
              </button>
            ))}
          </div>
        </div>

        {/* Message */}
        <div className="space-y-2">
          <Label htmlFor="ticket-message">Message *</Label>
          <textarea
            id="ticket-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Please describe your issue in detail..."
            disabled={isLoading}
            rows={5}
            className={cn(
              'w-full px-3 py-2 rounded-md',
              'border border-input bg-background',
              'text-sm resize-none',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              'placeholder:text-muted-foreground',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          />
          <Text variant="xs" color="muted">
            Minimum 10 characters ({message.length}/10)
          </Text>
        </div>

        {/* Submit */}
        <div className="flex gap-3 pt-2">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
          )}
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || isLoading}
            className={cn('flex-1 gap-2', !onCancel && 'w-full')}
          >
            {isLoading ? (
              <>
                <SFIcon icon={sfIcons.loader} className="h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <SFIcon icon={sfIcons.send} className="h-4 w-4" />
                Create Ticket
              </>
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
}
